import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { isInstalledPWA } from "@/utils/pwa";
import { cn } from "@/lib/utils";
import { useRouteTransitionStore } from "@/hooks/useRouteTransitionStore";
import { EnhancedHomeTabSkeleton } from "@/components/skeletons/EnhancedHomeTabSkeleton";
import {
  AppointmentsTabSkeleton,
  MedsTabSkeleton,
  MoreTabSkeleton,
  TrackTabSkeleton,
} from "@/components/skeletons/TabSkeletons";
import { Skeleton } from "@/components/ui/skeleton";

// Reduced from 180ms to 50ms to prevent feeling slow
// PWA needs some visual feedback; browser can be faster
const MIN_VISIBLE_MS = 50;
const HARD_TIMEOUT_MS = 5000;

const parseTarget = (target: string | null) => {
  if (!target) return null;
  try {
    return new URL(target, window.location.origin);
  } catch {
    return null;
  }
};

const getDashboardTab = (search: string) => {
  const tab = new URLSearchParams(search).get("tab") ?? "home";
  const allowed = new Set(["home", "track", "meds", "appointments", "beta", "more"]);
  return allowed.has(tab) ? tab : "home";
};

const DashboardTransitionSkeleton = ({ tab }: { tab: string }) => {
  switch (tab) {
    case "track":
      return <TrackTabSkeleton />;
    case "meds":
      return <MedsTabSkeleton />;
    case "appointments":
      return <AppointmentsTabSkeleton />;
    case "more":
      return <MoreTabSkeleton />;
    case "beta":
      // keep this lightweight so the overlay doesn't feel “heavy”
      return (
        <div className="space-y-4">
          <Skeleton className="h-8 w-48 bg-primary/10" />
          <Skeleton className="h-24 w-full bg-primary/10" />
          <Skeleton className="h-24 w-full bg-primary/10" />
        </div>
      );
    case "home":
    default:
      return <EnhancedHomeTabSkeleton />;
  }
};

/**
 * PWA-only: show a brief skeleton overlay during client-side navigations.
 * This improves perceived performance (no blank flash) and discourages double-taps.
 */
export const RouteTransitionOverlay = () => {
  const location = useLocation();
  const isTransitioning = useRouteTransitionStore((s) => s.isTransitioning);
  const target = useRouteTransitionStore((s) => s.target);
  const startedAt = useRouteTransitionStore((s) => s.startedAt);
  const stop = useRouteTransitionStore((s) => s.stop);

  const targetUrl = useMemo(() => parseTarget(target), [target]);

  // Auto-stop safety
  useEffect(() => {
    if (!isTransitioning) return;
    const t = window.setTimeout(() => stop(), HARD_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [isTransitioning, stop]);

  // Stop when we reach the target URL (or when location changes if target is unknown)
  useEffect(() => {
    if (!isTransitioning) return;

    const cur = `${location.pathname}${location.search}`;
    const tgt = targetUrl ? `${targetUrl.pathname}${targetUrl.search}` : null;

    if (!tgt || cur === tgt) {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
      const t = window.setTimeout(() => stop(), remaining);
      return () => window.clearTimeout(t);
    }
  }, [isTransitioning, location.pathname, location.search, startedAt, stop, targetUrl]);

  // Only show in installed PWA.
  if (!isTransitioning || !isInstalledPWA()) return null;

  const targetPath = targetUrl?.pathname ?? location.pathname;
  const targetSearch = targetUrl?.search ?? location.search;

  const isDashboard = targetPath === "/dashboard";
  const tab = isDashboard ? getDashboardTab(targetSearch) : null;

  return (
    <div
      className={cn(
        // Leave the bottom nav region uncovered so taps on the bottom nav
        // are never swallowed by the overlay during a transition.
        // Beta feedback: Rachel Tappert reported nav taps "often unresponsive"
        // — caused by overlay (pointer-events-auto) covering the nav while
        // a previous transition was still in flight.
        "fixed left-0 right-0 top-0 z-[60]",
        "bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] md:bottom-0",
        // Use solid semi-transparent background instead of backdrop-blur
        // to prevent blurry text/icons during transitions on mobile
        "bg-background/90",
        "pointer-events-auto",
        // Simple opacity fade for crisp transitions
        "transition-opacity duration-150 ease-out"
      )}
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      <div className="mx-auto max-w-4xl px-3 pt-4 pb-24">
        {isDashboard && tab ? (
          <DashboardTransitionSkeleton tab={tab} />
        ) : (
          <div className="space-y-4">
            <Skeleton className="h-8 w-40 bg-primary/10" />
            <Skeleton className="h-12 w-full bg-primary/10" />
            <Skeleton className="h-12 w-full bg-primary/10" />
            <Skeleton className="h-40 w-full bg-primary/10" />
          </div>
        )}
      </div>
      <span className="sr-only">Loading…</span>
    </div>
  );
};
